import {
  MARKETPLACE_WORKSPACE_COOKIE,
  normalizeMarketplaceWorkspacePath,
  parseMarketplaceWorkspaceSelection,
} from "@repo/utils/lib/marketplace-workspace-selection";

export { normalizeMarketplaceWorkspacePath as marketplaceWorkspacePath };
export { parseMarketplaceWorkspaceSelection as rememberedMarketplacePath };
const STORAGE_KEY = "venturu:marketplace-workspace";
const CHANGE_EVENT = "venturu:marketplace-workspace-changed";

function cookieSnapshot(): string {
  try {
    const value = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${MARKETPLACE_WORKSPACE_COOKIE}=`))
      ?.slice(MARKETPLACE_WORKSPACE_COOKIE.length + 1);
    return value ? decodeURIComponent(value) : "";
  } catch {
    return "";
  }
}

export function getMarketplaceWorkspaceSnapshot(): string {
  const cookie = cookieSnapshot();
  if (cookie) return cookie;
  try {
    return (
      window.localStorage.getItem(STORAGE_KEY) ??
      window.sessionStorage.getItem(STORAGE_KEY) ??
      ""
    );
  } catch {
    return "";
  }
}

export function setMarketplaceWorkspace(
  actorUserId: string,
  path: string | null,
) {
  const value = JSON.stringify({
    actorUserId,
    path: path ? normalizeMarketplaceWorkspacePath(path) : null,
  });
  const before = getMarketplaceWorkspaceSnapshot();
  // Keep an explicit cleared selection so an old tab cannot revive legacy state.
  try {
    const domain =
      location.hostname === "venturu.com" ||
      location.hostname.endsWith(".venturu.com")
        ? ";domain=venturu.com"
        : "";
    // biome-ignore lint/suspicious/noDocumentCookie: Must be synchronous before noopener navigation; Cookie Store is not universal.
    document.cookie = `${MARKETPLACE_WORKSPACE_COOKIE}=${encodeURIComponent(value)};path=/;max-age=2592000;samesite=lax${location.protocol === "https:" ? ";secure" : ""}${domain}`;
  } catch {
    /* Local storage also supports cookie-restricted browsers. */
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Cookie persistence still works when storage is restricted. */
  }
  if (before !== value) window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function migrateMarketplaceWorkspace(actorUserId?: string | null) {
  if (!actorUserId) return;
  try {
    if (cookieSnapshot() || window.localStorage.getItem(STORAGE_KEY)) return;
    const legacy = window.sessionStorage.getItem(STORAGE_KEY);
    const path = parseMarketplaceWorkspaceSelection(legacy ?? "", actorUserId);
    if (path) setMarketplaceWorkspace(actorUserId, path);
  } catch {
    /* Persistence is optional; authorization is not. */
  }
}

export function subscribeMarketplaceWorkspace(listener: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) listener();
  };
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", listener);
  window.addEventListener("pageshow", listener);
  document.addEventListener("visibilitychange", listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", listener);
    window.removeEventListener("pageshow", listener);
    document.removeEventListener("visibilitychange", listener);
  };
}
